import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  requestFcmToken,
  registerFcmTokenWithBackend,
  type FcmErrorReason,
} from "@/lib/firebase/fcm";
import {
  detectSupport,
  isTransientReason,
  nextBackoffMs,
  type FcmStatus,
} from "./fcm-status";

/** Max backoff attempts in a single trigger-cycle before we give up until the
 * next trigger (online / visibility / store-switch / manual retry). */
const MAX_ATTEMPTS_PER_CYCLE = 6;

type FcmLastError = { reason: FcmErrorReason } | null;

export type FcmRegistrationContextValue = {
  status: FcmStatus;
  lastError: FcmLastError;
  lastRegisteredAt: number | null;
  /** Manual retry — resets the backoff cycle and re-attempts even from a
   * terminal status (the user explicitly asked). */
  retry: () => void;
  /** Used by the banner: request OS permission, then attempt registration. */
  requestPermissionAndRegister: () => Promise<void>;
};

const FcmRegistrationContext = createContext<FcmRegistrationContextValue | null>(
  null,
);

export function useFcmRegistration(): FcmRegistrationContextValue {
  const ctx = useContext(FcmRegistrationContext);
  if (!ctx) {
    throw new Error(
      "useFcmRegistration must be used within a <FcmRegistrationProvider>",
    );
  }
  return ctx;
}

type FcmRegistrationProviderProps = {
  sellerId?: string;
  storeId?: string;
  children: React.ReactNode;
};

export function FcmRegistrationProvider({
  sellerId,
  storeId,
  children,
}: FcmRegistrationProviderProps) {
  // UI-driving state.
  const [status, setStatus] = useState<FcmStatus>("registering");
  const [lastError, setLastError] = useState<FcmLastError>(null);
  const [lastRegisteredAt, setLastRegisteredAt] = useState<number | null>(null);

  // Imperative refs (do not drive render).
  const inFlightRef = useRef<Promise<void> | null>(null);
  // A trigger that fires WHILE a run is in flight coalesces onto the existing
  // promise but sets this flag so the settle path starts ONE trailing rerun —
  // otherwise the trailing trigger's intent (e.g. the offline→online heal) is
  // silently dropped.
  const rerunRequestedRef = useRef(false);
  const attemptRef = useRef(0);
  const registeredKeyRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest seller/store, read inside async closures without re-binding listeners.
  const sellerIdRef = useRef(sellerId);
  const storeIdRef = useRef(storeId);
  // Guard against state updates after unmount.
  const mountedRef = useRef(true);

  useEffect(() => {
    sellerIdRef.current = sellerId;
    storeIdRef.current = storeId;
  }, [sellerId, storeId]);

  const clearPendingTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // ── Core: ensureRegistered ────────────────────────────────────────────────
  // Idempotent + in-flight guarded. Concurrent calls coalesce onto the single
  // in-flight promise. Declared as a ref-held function so the stable callbacks
  // below (triggers, retry) can call it without a changing identity.
  const ensureRegisteredRef = useRef<() => Promise<void>>(async () => {});

  const scheduleRetry = useCallback(() => {
    if (attemptRef.current >= MAX_ATTEMPTS_PER_CYCLE) return;
    const delay = nextBackoffMs(attemptRef.current);
    attemptRef.current += 1;
    clearPendingTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void ensureRegisteredRef.current();
    }, delay);
  }, [clearPendingTimer]);

  const runEnsureRegistered = useCallback(async (): Promise<void> => {
    // Support / permission guards (synchronous, terminal where applicable).
    if (!detectSupport()) {
      if (mountedRef.current) setStatus("unsupported");
      return;
    }

    const permission = Notification.permission;
    if (permission === "denied") {
      if (mountedRef.current) setStatus("denied");
      return;
    }
    if (permission === "default") {
      // Do NOT auto-request; the banner owns the prompt.
      if (mountedRef.current) setStatus("prompt");
      return;
    }

    // permission === 'granted' beyond here.
    // NOTE: we deliberately do NOT short-circuit on `navigator.onLine` here.
    // The flag is unreliable (can be stuck `false` while online), so
    // `requestFcmToken` probes real connectivity instead; a genuine offline is
    // surfaced below as the `offline` reason.

    if (mountedRef.current) {
      setStatus("registering");
      setLastError(null);
    }

    const result = await requestFcmToken();

    if (!result.ok) {
      if (mountedRef.current) setLastError({ reason: result.reason });
      const transient = isTransientReason(result.reason);
      if (result.reason === "offline") {
        // A probe-confirmed offline (requestFcmToken already verified real
        // connectivity). The 'online' event may never fire when the flag is
        // stuck, so schedule a backoff retry to keep re-probing — the 'online'
        // listener remains an additional fast-path.
        if (mountedRef.current) setStatus("offline");
        scheduleRetry();
        return;
      }
      if (transient) {
        if (mountedRef.current) setStatus("error");
        scheduleRetry();
      } else {
        // Terminal reason (permission / config) — no auto-retry.
        if (mountedRef.current) {
          setStatus(result.reason === "permission" ? "denied" : "error");
        }
      }
      return;
    }

    // Token obtained — dedupe against the last successfully-registered key.
    const key = `${sellerIdRef.current}:${storeIdRef.current}:${result.token}`;
    if (registeredKeyRef.current === key) {
      if (mountedRef.current) setStatus("registered");
      return;
    }

    const ok = await registerFcmTokenWithBackend(
      result.token,
      storeIdRef.current,
    );
    if (ok) {
      registeredKeyRef.current = key;
      if (mountedRef.current) {
        setLastRegisteredAt(Date.now());
        setStatus("registered");
      }
    } else {
      if (mountedRef.current) {
        setLastError({ reason: "network" });
        setStatus("error");
      }
      scheduleRetry();
    }
  }, [scheduleRetry]);

  // Wrap runEnsureRegistered with the in-flight coalescing guard.
  const ensureRegistered = useCallback((): Promise<void> => {
    // Coalesce concurrent calls onto the in-flight promise, but record that a
    // fresh re-attempt was requested so the settle path runs exactly one more.
    if (inFlightRef.current) {
      rerunRequestedRef.current = true;
      return inFlightRef.current;
    }
    const p = runEnsureRegistered().finally(() => {
      inFlightRef.current = null;
      // Trailing rerun: a trigger arrived mid-flight (e.g. `online` fired during
      // a slow prior attempt). Clear the flag BEFORE re-running so the rerun
      // re-arms it only if IT, too, gets concurrent calls — at most one rerun
      // per settle, no busy-loop. Guarded by `mounted` to avoid setState after
      // unmount. A reset trigger (online/store-switch/retry) already set
      // attemptRef=0 before requesting, so the trailing rerun runs a fresh cycle.
      if (rerunRequestedRef.current && mountedRef.current) {
        rerunRequestedRef.current = false;
        void ensureRegisteredRef.current();
      }
    });
    inFlightRef.current = p;
    return p;
  }, [runEnsureRegistered]);

  // Keep the ref pointing at the latest ensureRegistered so timers/listeners
  // bound to the ref always invoke the current closure.
  useEffect(() => {
    ensureRegisteredRef.current = ensureRegistered;
  }, [ensureRegistered]);

  // Reset the backoff cycle, then ensure. Used by fresh triggers (online,
  // store-switch, manual retry) so each gets a full set of attempts.
  const resetCycleAndEnsure = useCallback(() => {
    attemptRef.current = 0;
    clearPendingTimer();
    void ensureRegisteredRef.current();
  }, [clearPendingTimer]);

  // ── Triggers ──────────────────────────────────────────────────────────────

  // Mount + whenever sellerId/storeId change → reset cycle + ensure.
  useEffect(() => {
    resetCycleAndEnsure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId, storeId, resetCycleAndEnsure]);

  // window 'online' → reset cycle + ensure.
  useEffect(() => {
    const onOnline = () => resetCycleAndEnsure();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [resetCycleAndEnsure]);

  // document 'visibilitychange' (visible only) → ensure (no cycle reset; the
  // in-flight guard prevents storms).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void ensureRegisteredRef.current();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () =>
      document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Permissions API 'change' (feature-detected; fall back to visibility).
  useEffect(() => {
    let permStatus: PermissionStatus | null = null;
    const onChange = () => {
      void ensureRegisteredRef.current();
    };
    try {
      if (typeof navigator !== "undefined" && navigator.permissions) {
        navigator.permissions
          .query({ name: "notifications" as PermissionName })
          .then((status) => {
            permStatus = status;
            permStatus.addEventListener("change", onChange);
          })
          .catch(() => {
            // Unsupported descriptor (older Safari) — rely on visibility.
          });
      }
    } catch {
      // navigator.permissions absent — rely on visibility.
    }
    return () => {
      if (permStatus) permStatus.removeEventListener("change", onChange);
    };
  }, []);

  // Unmount cleanup: stop pending timers + flip the mounted guard.
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearPendingTimer();
    };
  }, [clearPendingTimer]);

  // ── Public API ────────────────────────────────────────────────────────────

  const retry = useCallback(() => {
    // Manual: ignore terminal-no-autoretry; a fresh cycle from the user.
    resetCycleAndEnsure();
  }, [resetCycleAndEnsure]);

  const requestPermissionAndRegister = useCallback(async () => {
    if (!detectSupport()) {
      if (mountedRef.current) setStatus("unsupported");
      return;
    }
    try {
      await Notification.requestPermission();
    } catch {
      // requestPermission can reject in some browsers; ensureRegistered will
      // re-derive status from the (unchanged) permission value.
    }
    attemptRef.current = 0;
    clearPendingTimer();
    await ensureRegisteredRef.current();
  }, [clearPendingTimer]);

  const value = useMemo<FcmRegistrationContextValue>(
    () => ({
      status,
      lastError,
      lastRegisteredAt,
      retry,
      requestPermissionAndRegister,
    }),
    [status, lastError, lastRegisteredAt, retry, requestPermissionAndRegister],
  );

  return (
    <FcmRegistrationContext.Provider value={value}>
      {children}
    </FcmRegistrationContext.Provider>
  );
}
